import type React from 'react';
import { useState } from 'react';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import CampaignIcon from '@mui/icons-material/Campaign';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { Box, Collapse, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { NOTIFICATION_TYPE } from 'context/NotificationCenterContext';
import type { AppNotification } from 'context/NotificationCenterContext';
import {
  GENERAL_NOTIFICATION_THEME,
  getFulfillmentNotificationTitle,
  getFulfillmentThemeForNotification,
  getGlobalEventTheme,
  getNotificationTitle,
  getWorkboardTaskNotificationTitle,
  getWorkboardTaskTheme,
  RECENT_NOTIFICATION_THRESHOLD_MS,
} from './NotificationCenter.constants';
import {
  cardInProgressInnerSx,
  cardInProgressSx,
  fileItemSx,
  filesToggleSx,
  getNotificationLinkChipSx,
  themedCardSurfaceSx,
  timestampSx,
  viewResultsLinkSx,
} from './NotificationCenter.styles';
import { NOTIFICATION_VARIANT } from './NotificationCenter.types';
import {
  formatNotificationRelativeTime,
  getNotificationLinks,
  getNotificationVariant,
  isValidUrl,
} from './NotificationCenter.utils';
import {
  getCardSurfaceSx,
  getNotificationIcon,
  getNotificationIconCircleSx,
  getNotificationTitleColor,
} from './NotificationCenter.variantUi';
import {
  JobNotificationMainText,
  NotificationDismissButton,
  NotificationSubtitle,
  NotificationUnreadColumn,
  ThemedNotificationBody,
} from './NotificationShared';

/** Strips origin from same-origin absolute URLs so React Router can navigate within the SPA. */
function resolveLinkPath(link: string): string | null {
  try {
    const url = new URL(link);
    if (url.origin === globalThis.location.origin) {
      return url.pathname + url.search + url.hash;
    }
    return null;
  } catch {
    return link.startsWith('/') ? link : null;
  }
}

interface NotificationCardProps {
  notification: AppNotification;
  onDismiss: (id: string) => void;
  /** Mark as read (click on card body); syncs via Ably when possible. */
  onMarkAsRead: (id: string) => void;
  onClose?: () => void;
  nowMs: number;
}

const NotificationCard = ({ notification, onDismiss, onMarkAsRead, onClose, nowMs }: NotificationCardProps) => {
  const [filesOpen, setFilesOpen] = useState(false);

  const { id, read } = notification;
  const relTime =
    notification.type === NOTIFICATION_TYPE.GLOBAL ||
    notification.type === NOTIFICATION_TYPE.GENERAL ||
    notification.type === NOTIFICATION_TYPE.FULFILLMENT ||
    notification.type === NOTIFICATION_TYPE.WORKBOARD_TASK
      ? formatNotificationRelativeTime(notification.updatedAt, nowMs)
      : formatNotificationRelativeTime(notification.job.timestamp, nowMs);

  const relTimeNode = relTime ? <Typography sx={timestampSx}>{relTime}</Typography> : null;

  const activateRead = () => {
    if (!read) {
      onMarkAsRead(id);
    }
  };

  const handleDismiss = () => {
    onDismiss(id);
  };

  const themedCardShellProps = {
    'data-testid': 'notification-card' as const,
    role: 'button' as const,
    tabIndex: 0,
    onClick: activateRead,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        activateRead();
      }
    },
  };

  if (notification.type === NOTIFICATION_TYPE.FULFILLMENT) {
    const theme = getFulfillmentThemeForNotification(notification);
    const title = getFulfillmentNotificationTitle(notification);
    const linkPath = notification.link ? resolveLinkPath(notification.link) : null;

    return (
      <Box {...themedCardShellProps} sx={themedCardSurfaceSx(theme)}>
        <ThemedNotificationBody
          theme={theme}
          icon={<AssignmentIndIcon sx={{ fontSize: 16, color: theme.iconColor }} />}
          title={title}
          timestamp={relTimeNode}
          showUnreadColumn
          read={!!read}
          onDismiss={handleDismiss}>
          {notification.companyName && <NotificationSubtitle>{notification.companyName}</NotificationSubtitle>}
          {linkPath && (
            <Box sx={{ mt: 0.75 }}>
              <Typography
                component={Link}
                to={linkPath}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  activateRead();
                  onClose?.();
                }}
                sx={getNotificationLinkChipSx(theme)}>
                View Workboard
              </Typography>
            </Box>
          )}
        </ThemedNotificationBody>
      </Box>
    );
  }

  if (notification.type === NOTIFICATION_TYPE.WORKBOARD_TASK) {
    const theme = getWorkboardTaskTheme();
    const title = getWorkboardTaskNotificationTitle(notification);
    const linkPath = notification.link ? resolveLinkPath(notification.link) : null;

    return (
      <Box {...themedCardShellProps} sx={themedCardSurfaceSx(theme)}>
        <ThemedNotificationBody
          theme={theme}
          icon={<AssignmentIndIcon sx={{ fontSize: 16, color: theme.iconColor }} />}
          title={title}
          timestamp={relTimeNode}
          showUnreadColumn
          read={!!read}
          onDismiss={handleDismiss}>
          {notification.taskName && <NotificationSubtitle>{notification.taskName}</NotificationSubtitle>}
          {linkPath && (
            <Box sx={{ mt: 0.75 }}>
              <Typography
                component={Link}
                to={linkPath}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  onClose?.();
                }}
                sx={getNotificationLinkChipSx(theme)}>
                View Calendar
              </Typography>
            </Box>
          )}
        </ThemedNotificationBody>
      </Box>
    );
  }

  if (notification.type === NOTIFICATION_TYPE.GLOBAL || notification.type === NOTIFICATION_TYPE.GENERAL) {
    const theme =
      notification.type === NOTIFICATION_TYPE.GLOBAL
        ? getGlobalEventTheme(notification.eventType)
        : GENERAL_NOTIFICATION_THEME;
    const AnnouncementIcon = notification.type === NOTIFICATION_TYPE.GLOBAL ? CampaignIcon : NotificationsIcon;
    const linkUrl = notification.link && isValidUrl(notification.link) ? notification.link : null;
    const linkLabel = linkUrl ? (notification.linkLabel ?? 'View') : null;

    return (
      <Box {...themedCardShellProps} sx={themedCardSurfaceSx(theme)}>
        <ThemedNotificationBody
          theme={theme}
          icon={<AnnouncementIcon sx={{ fontSize: 16, color: theme.iconColor }} />}
          title={notification.title}
          timestamp={relTimeNode}
          showUnreadColumn
          read={!!read}
          onDismiss={handleDismiss}>
          <NotificationSubtitle>{notification.content}</NotificationSubtitle>
          {linkUrl && (
            <Box sx={{ mt: 0.75 }}>
              <Box
                component="a"
                href={linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  onClose?.();
                }}
                sx={getNotificationLinkChipSx(theme)}>
                {linkLabel}
              </Box>
            </Box>
          )}
        </ThemedNotificationBody>
      </Box>
    );
  }

  // After global/general early return, TypeScript narrows notification to JobAppNotification
  const jobNotification = notification;
  const { job, updatedAt } = jobNotification;
  const variant = getNotificationVariant(notification);
  const isRecent = nowMs - updatedAt.getTime() < RECENT_NOTIFICATION_THRESHOLD_MS;
  const resultLinks = getNotificationLinks(notification);
  const resultUrl = resultLinks?.primary ?? null;

  const { PROCESSING, FAILED } = NOTIFICATION_VARIANT;

  const cardSx = getCardSurfaceSx(variant, isRecent, { read: jobNotification.read });
  // Failed cards always have white background regardless of read state
  const cardSxFinal = variant === FAILED ? { ...cardSx, background: '#fff', backgroundColor: '#fff' } : cardSx;
  const iconCircle = getNotificationIconCircleSx(variant);
  const icon = getNotificationIcon(variant);
  const title = getNotificationTitle(variant);
  const titleColor = getNotificationTitleColor(variant);

  const mainTextColumn = (
    <JobNotificationMainText
      variant={variant}
      job={job}
      title={title}
      titleColor={titleColor}
      timestamp={relTimeNode}
      startedExtra={
        job.filenames && job.filenames.length > 0 ? (
          <Box mt={0.75}>
            <Box
              component="button"
              sx={filesToggleSx}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                setFilesOpen(o => !o);
              }}>
              {filesOpen ? '▾' : '▸'} {job.filenames.length} file{job.filenames.length === 1 ? '' : 's'}
            </Box>
            <Collapse in={filesOpen}>
              <Box sx={{ mt: 0.5, pl: 1.5, display: 'flex', flexDirection: 'column', gap: 0.25, overflow: 'hidden' }}>
                {job.filenames.map((file, i) => (
                  <Typography key={i} sx={fileItemSx}>
                    {file}
                  </Typography>
                ))}
              </Box>
            </Collapse>
          </Box>
        ) : null
      }
      resultsExtra={
        resultLinks ? (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
            <Typography
              component={Link}
              to={resultLinks.primary}
              onClick={e => {
                e.stopPropagation();
                activateRead();
                onClose?.();
              }}
              sx={viewResultsLinkSx}>
              {resultLinks.primaryLabel}
            </Typography>
            {resultLinks.secondary && (
              <Typography
                component={Link}
                to={resultLinks.secondary}
                onClick={e => {
                  e.stopPropagation();
                  activateRead();
                  onClose?.();
                }}
                sx={viewResultsLinkSx}>
                {resultLinks.secondaryLabel}
              </Typography>
            )}
          </Box>
        ) : null
      }
    />
  );

  const iconAndBodyTwoCol = (
    <>
      <Box sx={iconCircle}>{icon}</Box>
      {mainTextColumn}
    </>
  );

  const fourColumnRow = (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto auto', gap: 1, alignItems: 'flex-start' }}>
      {iconAndBodyTwoCol}
      <NotificationUnreadColumn read={!!read} />
      <NotificationDismissButton onClick={handleDismiss} />
    </Box>
  );

  /** Unread PROCESSING: animated gradient frame (not the flat unread palette from getCardSurfaceSx). */
  if (variant === PROCESSING && !read) {
    return (
      <Box data-testid="notification-card" sx={cardInProgressSx}>
        <Box
          sx={{ ...cardInProgressInnerSx, cursor: 'pointer' }}
          role="button"
          tabIndex={0}
          onClick={activateRead}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              activateRead();
            }
          }}>
          {fourColumnRow}
        </Box>
      </Box>
    );
  }

  /** Do not wrap the dismiss IconButton in <Link> — nested interactive elements navigate incorrectly. */
  if (resultUrl) {
    return (
      <Box
        data-testid="notification-card"
        sx={{
          ...cardSxFinal,
          display: 'grid',
          gridTemplateColumns: '1fr auto auto',
          gap: 1,
          alignItems: 'flex-start',
        }}>
        <Link
          to={resultUrl}
          onClick={() => {
            activateRead();
            onClose?.();
          }}
          style={{ textDecoration: 'none', display: 'block', color: 'inherit' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              gap: 1,
              alignItems: 'flex-start',
              cursor: 'pointer',
              '&:hover': { opacity: 0.93 },
            }}>
            {iconAndBodyTwoCol}
          </Box>
        </Link>
        <NotificationUnreadColumn read={!!read} />
        <NotificationDismissButton onClick={handleDismiss} />
      </Box>
    );
  }

  return (
    <Box
      data-testid="notification-card"
      sx={{ ...cardSxFinal, cursor: 'pointer' }}
      role="button"
      tabIndex={0}
      onClick={activateRead}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          activateRead();
        }
      }}>
      {fourColumnRow}
    </Box>
  );
};

export default NotificationCard;
